
import { useRef, useCallback, useState, useMemo, Dispatch, SetStateAction } from 'react'
import { ArrowSvg } from '../svgs/Svgs'
import './OmniSelect.scss'
import CustomToolTip from '../CustomToolTip'
import { mergeClassNames } from '../../../utils/commonFunctions'
import useClickOutside from './hooks/useClickOutside'
import useKeyboardNavigation from './hooks/useKeyboardNavigation'
import { OverlayTrigger } from 'react-bootstrap'

interface OmniSelectProps {
    id?: string
    className?: string
    placement?: 'top' | 'bottom' | 'left' | 'right'
    options: string[]
    value: string
    onChange: (value: string) => void;
    onNavigate?: (direction: 'left' | 'right') => void;
    isPrevDisabled?: boolean;
    isNextDisabled?: boolean;
    disabled?: boolean;
    hideNavigation?: boolean;
    icon?: React.ReactNode;
    headerOption?: {
        title: string;
        description?: string;
        icon: React.ReactNode;
    }
    open: null | string
    setOpen: Dispatch<SetStateAction<null | string>>
}

const uniqueId = 'omni-select-' + Math.random().toString(36).slice(2);

const OmniSelect: React.FC<OmniSelectProps> = ({
    id = uniqueId,
    placement = 'top',
    open,
    setOpen,
    options,
    value,
    headerOption,
    onChange,
    onNavigate,
    isPrevDisabled,
    isNextDisabled,
    className = '',
    disabled = false,
    hideNavigation = false,
    icon
}) => {
    const triggerRef = useRef<HTMLDivElement | null>(null)
    const optionContainerRef = useRef<HTMLDivElement | null>(null)

    const [activeIndex, setActiveIndex] = useState<number>(-1)
    const selectedIndex = useMemo(() => {
        const exact = options.findIndex(option => option === value);
        if (exact !== -1) return exact;
        // If value is a range like "Orbit 1-4", match the first orbit number
        const match = value.match(/\d+/);
        if (match) {
            const num = match[0];
            const approx = options.findIndex(option => option === `Orbit ${num}`);
            if (approx !== -1) return approx;
        }
        return -1;
    }, [options, value])

    const resetState = useCallback(() => {
        setOpen(null)
        setActiveIndex(-1)
    }, [])

    const handleSelectChange = useCallback((selectedValue: string) => {
        onChange(selectedValue)
        resetState()
    }, [onChange, resetState])

    const handleNavigate = useCallback((side: 'left' | 'right') => {
        if (onNavigate) {
            onNavigate(side);
            return;
        }
        if (side === 'left' && selectedIndex > 0) {
            onChange(options[selectedIndex - 1])
        } else if (side === 'right' && selectedIndex < options.length - 1) {
            onChange(options[selectedIndex + 1])
        }
    }, [selectedIndex, options, onChange, onNavigate])

    // behavior hooks

    const setActiveIndexUpdater = useCallback(
        (indexOrUpdater: number | ((prev: number) => number)) =>
            setActiveIndex(prev => (typeof indexOrUpdater === 'function' ? (indexOrUpdater as (p: number) => number)(prev) : indexOrUpdater)),
        [setActiveIndex]
    )

    useClickOutside(triggerRef, optionContainerRef, open, resetState)
    useKeyboardNavigation(open, options, activeIndex, setOpen, setActiveIndexUpdater, handleSelectChange)

    return (
        <OverlayTrigger
            placement={placement}
            show={open === id}
            overlay={
                <div className='omni__select__options__container'
                    ref={optionContainerRef}
                    id={id}
                >
                    {headerOption && (
                        <div className='omni__select__options__container__header'>
                            {headerOption.icon && <div className='option__header__icon'>{headerOption.icon}</div>}
                            <div className='option__header__details'>
                                <span className='option__header__title'>{headerOption.title}</span>
                                {headerOption.description && <span className='option__header__description'>{headerOption.description}</span>}
                            </div>
                        </div>
                    )}
                    {options.map((option, idx) => (
                        <div className={mergeClassNames('select__option',
                            activeIndex === idx && 'focused',
                            option === value && 'selected'
                        )}
                            key={option}
                            onClick={() => handleSelectChange(option)}
                            onMouseEnter={() => setActiveIndex(idx)}
                            role="option"
                            aria-selected={activeIndex === idx}
                            tabIndex={activeIndex === idx ? 0 : -1}
                        >{option}</div>
                    ))}
                </div>
            }
        >
            <div className={mergeClassNames(`omni__select__container ${disabled ? 'disabled' : ''}`, className)}>
                {!hideNavigation &&
                    <button className='omni__select__left__button'
                        disabled={isPrevDisabled ?? (selectedIndex === 0)}
                        onClick={() => handleNavigate('left')}
                        aria-label="Previous option"
                        title="Previous option"
                    >
                        <ArrowSvg side='left' />
                    </button>
                }
                <div className={`omni__select__dropdown__container`} ref={triggerRef}
                    role="button"
                    onClick={(event) => {
                        event.stopPropagation()
                        setOpen(prev => prev === id ? null : id)
                    }}
                >
                    <CustomToolTip title={value}>
                        <div className={`omni__select__dropdown__trigger ${icon ? 'size__icon' : ''}`}
                            tabIndex={0}
                        >
                            {icon || value}
                        </div>
                    </CustomToolTip>
                </div>
                {!hideNavigation &&
                    <button className='omni__select__right__button'
                        disabled={isNextDisabled ?? (selectedIndex === options.length - 1)}
                        onClick={() => handleNavigate('right')}
                        aria-label="Next option"
                        title="Next option"
                    >
                        <ArrowSvg side='right' />
                    </button>
                }
            </div>
        </OverlayTrigger>
    )
}

export default OmniSelect